import { FileImage, Link as LinkIcon, Upload, Users2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { ROUTES } from '@/config/constants';
import { Avatar, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import type { Profile } from '@/types';
import { displayName } from '@/features/communities/community-view/cvHelpers';
import { stripHandle } from '@/features/profile/helpers';
import { getEmblem, getTitle } from '@/features/profile/cosmetics';
import { EmblemGlyph } from '@/features/profile/components/EmblemGlyph';
import { useFriendship } from '@/features/profile/hooks/useSocial';
import { useProfileTheme } from '@/features/profile/hooks/useProfileTheme';
import { ProfileBannerSurface } from '@/components/theme/ProfileBannerSurface';
import { gradientTextStyle } from '@/components/theme/CommunityThemeScope';
import { avatarFrameStyle } from '@/features/studio/model/profileDoc';
import { useAuth } from '@/app/providers';
import { useDirectMessages } from '../hooks/useDirectMessages';

const URL_RE = /https?:\/\/[^\s<>"']+/gi;

export function FriendshipInfoRail({
  profile,
  conversationId,
  topActions,
}: {
  profile: Profile;
  conversationId: string | undefined;
  topActions?: ReactNode;
}) {
  const { user } = useAuth();
  const { data: friendship } = useFriendship(user?.id, profile.id);
  const { data: messages = [], isLoading } = useDirectMessages(conversationId);

  const title = getTitle(profile.title_id);
  const emblem = getEmblem(profile.emblem_id);
  const profileTheme = useProfileTheme(profile.id, emblem.glow);
  const name = displayName(profile);
  const handle = stripHandle(profile.handle);
  const media = messages.filter((m) => m.mediaUrl).slice(-9).reverse();
  const links = [...new Set(messages.flatMap((m) => (m.body ?? '').match(URL_RE) ?? []))].slice(-8).reverse();

  function shareProfile() {
    if (!handle) return;
    void navigator.clipboard?.writeText(`${window.location.origin}${ROUTES.profile(handle)}`);
    toast('Profile link copied.');
  }

  return (
    <aside className="relative flex h-full min-h-0 flex-col overflow-visible bg-[rgba(17,18,24,0.58)] shadow-[0_18px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl backdrop-saturate-[1.25]">
      {topActions && <div className="absolute right-3 top-3 z-[30] flex items-center gap-2">{topActions}</div>}
      <div className="relative h-40 flex-none overflow-hidden">
        <ProfileBannerSurface theme={profileTheme} fallbackImage={title.bg} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-[rgba(17,18,24,0.95)]" />
      </div>

      <div className="relative z-[1] -mt-12 flex-none px-5 pb-4">
        <div className="flex items-end justify-between gap-3">
          <Avatar
            src={profile.avatar_url}
            name={name}
            size={88}
            className="relative z-[2] border-[4px] border-[#111218] shadow-[0_12px_34px_rgba(0,0,0,0.5)]"
            style={
              profileTheme.doc.frame !== 'none'
                ? avatarFrameStyle(profileTheme.doc.frame, profileTheme.accent)
                : undefined
            }
          />
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={shareProfile}
              className="grid h-9 w-9 place-items-center rounded-lg text-white/70 transition-all hover:bg-white/[0.06] hover:text-accent"
              title="Share profile"
              aria-label="Share profile"
            >
              <Upload size={16} />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <h2
            className="min-w-0 truncate text-[22px] font-black tracking-[-0.4px] text-hi"
            style={profileTheme.doc.nameStyle === 'gradient' ? gradientTextStyle(profileTheme.accent) : undefined}
          >
            {name}
          </h2>
          {profileTheme.doc.showEmblem && <EmblemGlyph emblem={emblem} size={19} />}
        </div>
        {handle && <div className="mt-0.5 text-[13px] font-semibold text-lo">@{handle}</div>}
        {(profile.headline || profile.status) && (
          <p className="mt-2 line-clamp-2 text-[13px] font-medium leading-relaxed text-white/65">
            {profile.headline || profile.status}
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {!friendship && (
          <div className="mb-4 flex items-center gap-2 rounded-md bg-white/[0.035] p-3 text-[12px] font-medium leading-relaxed text-white/42">
            <Users2 size={15} className="text-white/35" />
            Nothing to display since you guys are not friends yet.
          </div>
        )}

        <RailSection title="Images sent" count={media.length} className="mt-4">
          {isLoading ? (
            <Loading />
          ) : media.length ? (
            <div className="grid grid-cols-3 gap-1.5">
              {media.map((m) => (
                <a key={m.id} href={m.mediaUrl ?? undefined} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
                  <img src={m.mediaUrl ?? ''} alt="" className="h-full w-full object-cover transition-transform duration-200 hover:scale-105" loading="lazy" />
                </a>
              ))}
            </div>
          ) : (
            <Empty icon={<FileImage size={16} />}>Images you send each other will collect here.</Empty>
          )}
        </RailSection>

        <RailSection title="Links sent" count={links.length} className="mt-4">
          {isLoading ? (
            <Loading />
          ) : links.length ? (
            <div className="space-y-2">
              {links.map((href) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-w-0 items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-[12px] font-semibold text-white/70 transition-colors hover:border-accent/30 hover:text-accent"
                >
                  <LinkIcon size={14} className="flex-none" />
                  <span className="truncate">{href.replace(/^https?:\/\//, '')}</span>
                </a>
              ))}
            </div>
          ) : (
            <Empty icon={<LinkIcon size={16} />}>Links shared in this DM will show up here.</Empty>
          )}
        </RailSection>
      </div>
    </aside>
  );
}

function RailSection({ title, count, className, children }: { title: string; count: number; className?: string; children: React.ReactNode }) {
  return (
    <section className={className}>
      <div className="mb-2 flex items-center justify-between text-[11px] font-black uppercase tracking-[0.75px] text-white/45">
        <span>{title}</span>
        <span>{count}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.035] p-3 text-[12px] font-medium leading-relaxed text-white/45">
      <span className="text-white/35">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function Loading() {
  return (
    <div className="grid h-24 place-items-center rounded-md border border-white/10 bg-white/[0.035] text-white/45">
      <Spinner />
    </div>
  );
}
